import { useState, useEffect } from 'react';
import { api } from '../services/api';

interface Fact {
  id: number;
  topic: string;
  disease: string | null;
  content_en: string;
  content_hausa: string | null;
  content_yoruba: string | null;
  content_igbo: string | null;
  content_french: string | null;
  source: string | null;
  verified_by: string | null;
  created_at: string;
  is_active: number;
}

export default function Facts() {
  const [facts, setFacts] = useState<Fact[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({
    topic: '', disease: '', content_en: '', content_hausa: '',
    content_yoruba: '', content_igbo: '', content_french: '',
    source: '', verified_by: '',
  });
  const [saving, setSaving] = useState(false);

  const loadFacts = () => {
    api.getFacts()
      .then(data => { setFacts(data.facts); setLoading(false); })
      .catch(() => setLoading(false));
  };

  useEffect(() => { loadFacts(); }, []);

  const handleAdd = async () => {
    if (!form.topic || !form.content_en) return;
    setSaving(true);
    try {
      await api.createFact(form);
      setShowAdd(false);
      setForm({ topic: '', disease: '', content_en: '', content_hausa: '', content_yoruba: '', content_igbo: '', content_french: '', source: '', verified_by: '' });
      loadFacts();
    } catch (e) {
      console.error('Failed to create fact:', e);
    }
    setSaving(false);
  };

  const handleDelete = async (id: number) => {
    if (!confirm('Delete this fact?')) return;
    try {
      await api.deleteFact(id);
      loadFacts();
    } catch (e) {
      console.error('Failed to delete fact:', e);
    }
  };

  if (loading) return <div style={{ padding: 32, color: '#64748b' }}>Loading facts...</div>;

  return (
    <div style={{ padding: 32 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 32 }}>
        <div>
          <h1 style={{ fontSize: 28, fontWeight: 700, marginBottom: 8, color: '#0f172a' }}>Facts</h1>
          <p style={{ color: '#64748b' }}>Verified health facts in the database ({facts.length} total)</p>
        </div>
        <button onClick={() => setShowAdd(!showAdd)} style={{
          padding: '10px 20px', borderRadius: 8, border: 'none',
          background: 'linear-gradient(135deg, #0891b2, #06b6d4)',
          color: '#fff', fontSize: 14, fontWeight: 600, cursor: 'pointer',
        }}>+ Add Fact</button>
      </div>

      {/* Add Fact Form */}
      {showAdd && (
        <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, padding: 24, marginBottom: 24 }}>
          <h2 style={{ fontSize: 18, fontWeight: 600, marginBottom: 16, color: '#0f172a' }}>Add New Fact</h2>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
            {[
              { key: 'topic', label: 'Topic *', placeholder: 'e.g. malaria, cholera, lassa_fever' },
              { key: 'disease', label: 'Disease Name', placeholder: 'e.g. Malaria' },
              { key: 'source', label: 'Source', placeholder: 'e.g. WHO, NCDC, CDC' },
              { key: 'verified_by', label: 'Verified By', placeholder: 'e.g. Dr. Smith' },
            ].map(f => (
              <div key={f.key}>
                <label style={{ display: 'block', fontSize: 13, fontWeight: 500, color: '#334155', marginBottom: 4 }}>{f.label}</label>
                <input
                  value={(form as any)[f.key]}
                  onChange={e => setForm({ ...form, [f.key]: e.target.value })}
                  placeholder={f.placeholder}
                  style={{ width: '100%', padding: '8px 12px', border: '1px solid #e2e8f0', borderRadius: 6, fontSize: 14 }}
                />
              </div>
            ))}
          </div>
          <div style={{ marginTop: 16 }}>
            <label style={{ display: 'block', fontSize: 13, fontWeight: 500, color: '#334155', marginBottom: 4 }}>English Content *</label>
            <textarea
              value={form.content_en}
              onChange={e => setForm({ ...form, content_en: e.target.value })}
              placeholder="Health fact in English..."
              rows={3}
              style={{ width: '100%', padding: '8px 12px', border: '1px solid #e2e8f0', borderRadius: 6, fontSize: 14, resize: 'vertical' }}
            />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 16, marginTop: 12 }}>
            {[
              { key: 'content_hausa', label: 'Hausa' },
              { key: 'content_yoruba', label: 'Yoruba' },
              { key: 'content_igbo', label: 'Igbo' },
            ].map(f => (
              <div key={f.key}>
                <label style={{ display: 'block', fontSize: 13, fontWeight: 500, color: '#334155', marginBottom: 4 }}>{f.label}</label>
                <textarea
                  value={(form as any)[f.key] || ''}
                  onChange={e => setForm({ ...form, [f.key]: e.target.value })}
                  placeholder={`${f.label} translation...`}
                  rows={2}
                  style={{ width: '100%', padding: '8px 12px', border: '1px solid #e2e8f0', borderRadius: 6, fontSize: 14, resize: 'vertical' }}
                />
              </div>
            ))}
          </div>
          <div style={{ display: 'flex', gap: 12, marginTop: 16 }}>
            <button onClick={handleAdd} disabled={saving || !form.topic || !form.content_en} style={{
              padding: '10px 24px', borderRadius: 8, border: 'none',
              background: (!form.topic || !form.content_en) ? '#cbd5e1' : 'linear-gradient(135deg, #059669, #10b981)',
              color: '#fff', fontSize: 14, fontWeight: 600, cursor: (!form.topic || !form.content_en) ? 'not-allowed' : 'pointer',
            }}>{saving ? 'Saving...' : 'Save Fact'}</button>
            <button onClick={() => setShowAdd(false)} style={{
              padding: '10px 24px', borderRadius: 8, border: '1px solid #e2e8f0',
              background: '#fff', color: '#64748b', fontSize: 14, cursor: 'pointer',
            }}>Cancel</button>
          </div>
        </div>
      )}

      {/* Facts List */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {facts.map(fact => (
          <div key={fact.id} style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, padding: 20 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 10 }}>
              <div style={{ display: 'flex', gap: 8 }}>
                <span style={{ fontSize: 12, padding: '3px 10px', borderRadius: 6, background: '#ede9fe', color: '#7c3aed', fontWeight: 500 }}>
                  {fact.topic.replace(/_/g, ' ')}
                </span>
                {fact.source && (
                  <span style={{ fontSize: 12, padding: '3px 10px', borderRadius: 6, background: '#f0f9ff', color: '#0891b2', fontWeight: 500 }}>
                    {fact.source}
                  </span>
                )}
                <span style={{
                  fontSize: 12, padding: '3px 10px', borderRadius: 6,
                  background: fact.is_active ? '#dcfce7' : '#fee2e2',
                  color: fact.is_active ? '#166534' : '#991b1b', fontWeight: 500,
                }}>{fact.is_active ? 'Active' : 'Inactive'}</span>
              </div>
              <button onClick={() => handleDelete(fact.id)} style={{
                padding: '4px 12px', borderRadius: 6, border: '1px solid #fecaca',
                background: '#fff', color: '#dc2626', fontSize: 12, cursor: 'pointer',
              }}>Delete</button>
            </div>
            <div style={{ fontSize: 14, color: '#334155', lineHeight: 1.6, marginBottom: 8 }}>{fact.content_en}</div>
            {(fact.content_hausa || fact.content_yoruba) && (
              <div style={{ fontSize: 12, color: '#94a3b8', display: 'flex', gap: 16 }}>
                {fact.content_hausa && <span>🇳🇬 Hausa: {fact.content_hausa.substring(0, 80)}...</span>}
                {fact.content_yoruba && <span>🇳🇬 Yoruba: {fact.content_yoruba.substring(0, 80)}...</span>}
              </div>
            )}
            <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 8 }}>
              Created: {new Date(fact.created_at).toLocaleDateString()}
              {fact.verified_by && ` • Verified by: ${fact.verified_by}`}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}